<script setup lang="ts">
import { SelectedPCPOption } from './ProfessionalCarePlan.vue'

type quickviewProps = {
  isEditMode?: boolean
  productStoreLocation: ProductStoreLocation
}

provide('location', LOCATIONS.quickView)
const emit = defineEmits(['close'])
const props = defineProps<quickviewProps>()
const productStore = useProductStore(props.productStoreLocation)
const basketsStore = useBasketsStore()
const { addToBagAction, updateItemBagAction } = useCartActions()
const userStore = useUserStore()
const i18n = useI18n()
const parentLocation = inject('location', LOCATIONS.content)
const lowercaseLocation = parentLocation?.toLowerCase()
const variationImages = computed(() =>
  findImageArray(productStore.selectedProduct?.images, productStore.selectedProduct?.variationValues),
)
const productUrl = computed(() => {
  return getProductUrl({
    productId: productStore.selectedProduct?.id,
    productName: productStore.selectedProduct?.name,
    locale: i18n.locale.value,
    slugUrl: productStore.selectedProduct?.slugUrl,
  })
})

const constructorItemParam = computed<RequiredItemParam>(() => ({
  itemId: productStore.selectedProductConstructorId || '',
  itemName: productStore.selectedProduct?.name || '',
  variationId: productStore.selectedProduct?.id || '',
}))

function onPcpSelected(option: SelectedPCPOption) {
  productStore.setPcpOption(option)
}

async function addToBag() {
  if (!productStore.selectedProduct) return
  if (productStore.selectedProduct.c_mHJRetailCategory.includes('Rings') && !productStore.selectedRingSize) {
    productStore.setRingSizeError(i18n.t('product.variant_selector.ring_size_add_to_bag_error_text'))
    return
  }

  const specialOrderSKU = productStore.selectedProcessedSizeOptions?.isSpecialOrder
    ? productStore.selectedProcessedSizeOptions?.specialOrderSKU
    : undefined

  const optionItems = []
  if (productStore.selectedPcpOption?.id) {
    optionItems.push({ optionId: 'pcp', optionValueId: productStore.selectedPcpOption.id })
  }
  if (productStore.selectedProduct?.c_engravable) {
    optionItems.push(engravingOptionItem(productStore.engravingText, userStore.isLoggedIn))
  }

  await addToBagAction(
    {
      productId: productStore.selectedProcessedSizeOptions?.sfccVariantSKU || productStore.selectedProduct?.id,
      specialOrderSKU,
      size: productStore.selectedRingSize ?? undefined,
      engravingText: productStore.engravingText ?? undefined,
      optionItems,
      popup: {
        product: productStore.selectedProduct,
        selectedPcpOption: productStore.selectedPcpOption || null,
      },
    },
    {
      product: productStore.selectedProduct,
      group: 'Content Action',
      location: 'Content Action',
    },
  )
  emit('close')
}

async function updateItem() {
  if (!productStore.selectedBasketItemId) return

  const specialOrderSKU = productStore.selectedProcessedSizeOptions?.isSpecialOrder
    ? productStore.selectedProcessedSizeOptions?.specialOrderSKU
    : undefined

  const optionItems = []
  if (productStore.selectedPcpOption?.id) {
    optionItems.push({ optionId: 'pcp', optionValueId: productStore.selectedPcpOption.id })
  }
  if (productStore.selectedProduct?.c_engravable) {
    optionItems.push(engravingOptionItem(productStore.engravingText, userStore.isLoggedIn))
  }

  await updateItemBagAction(
    {
      itemId: productStore.selectedBasketItemId,
      productId: productStore.selectedProcessedSizeOptions?.sfccVariantSKU || productStore.selectedProduct?.id,
      specialOrderSKU,
      size: productStore.selectedRingSize ?? undefined,
      engravingText: productStore.engravingText ?? undefined,
      optionItems,
    },
    {
      location: lowercaseLocation,
      product: productStore.selectedProduct,
    },
  )

  emit('close')
}
</script>

<template>
  <div class="quickview" data-testid="quickview">
    <div class="quick-view__product-title">
      <h3 class="quickview__name" data-testid="quickview__name">{{ productStore.selectedProduct?.name }}</h3>
      <span class="quickview__sku">{{ $t('my_bag.quick_view.sku') }} {{ productStore.selectedProduct?.id }}</span>
    </div>
    <div class="quickview__images-wrapper">
      <ImagesSwiper
        class="quickview__images"
        :images="variationImages"
        :video="productStore.selectedProduct?.c_videoRef"
        :sku="productStore.selectedProduct?.id"
        :constructorItemParam
        :is-quick-view="true"
      />
    </div>
    <Pricing
      class="quickview__pricing"
      :price="productStore.selectedProduct?.price"
      :tieredPrices="productStore.selectedProduct?.tieredPrices"
    />

    <div class="quickview__actions-wrapper">
      <PromotionList
        class="quickview__promotion-list"
        :promotions="productStore.selectedProduct?.productPromotions"
        :key="productStore.selectedProduct?.id"
      />
      <ProductVariantSelectors
        class="quickview__product-variant-selectors"
        :productStoreLocation="productStoreLocation"
      />
      <Engraving
        class="quickview__engraving"
        :productStoreLocation="productStoreLocation"
        :loading="basketsStore.loading"
        @submit="isEditMode && updateItem()"
      />
      <QuickviewLeadTime v-if="isEditMode" :product-store-location="productStoreLocation" />
      <ProductAvailability v-else :product-store-location="productStoreLocation" />
      <Button
        class="quickview__add-to-bag"
        data-testid="quickview__add-to-bag"
        variant="primary"
        @click="!isEditMode ? addToBag() : updateItem()"
        :disabled="basketsStore.loading"
      >
        {{ !isEditMode ? $t('product_details_page.add_to_bag.button.text') : $t('my_bag.quick_view.button.update') }}
      </Button>
      <Anchor class="quickview__view-details" variant="text" :url="productUrl" :new-tab="true">
        {{ $t('my_bag.quick_view.button.view_details') }}
      </Anchor>
      <ProfessionalCarePlan
        v-if="productStore.selectedProduct"
        :product="productStore.selectedProduct"
        :selectedPcpOptionId="productStore.selectedPcpOption?.id"
        :key="productStore.selectedPcpOption?.id"
        @pcp-selected="onPcpSelected"
      />
    </div>
  </div>
</template>

<style lang="postcss">
.quickview {
  padding-bottom: var(--space-70);

  .quickview__sku {
    font-size: var(--text-small);
  }

  .quickview__pricing.pricing {
    font-size: var(--text-xxlarge);
  }

  .quickview__images-wrapper {
    position: relative;
  }

  .quickview__actions-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
    margin-top: var(--space-30);
  }

  .quickview__add-to-bag {
    width: 100%;
    max-width: 100%;
  }

  .quickview__view-details {
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
